// ConfirmDialog — a small modal with an X, OK and Cancel. X and Cancel both call onCancel.
// Used by the sign-up page (send the verification email?) and the verify-email page (confirm this is your email?).
// Colors come from the page that opens it, so it follows the agent's branding.
import React, { useEffect } from 'react';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: string;
  okLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  /** A line shown under the message, for example "Verification isn't available right now." */
  notice?: string;
  noticeRole?: 'alert' | 'status';
  onOk: () => void;
  onCancel: () => void;
  colors: { background: string; text: string; buttonBackground: string; buttonText: string };
};

export function ConfirmDialog({
  open,
  title,
  message,
  okLabel = 'OK',
  cancelLabel = 'Cancel',
  busy = false,
  notice,
  noticeRole = 'status',
  onOk,
  onCancel,
  colors,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const button: React.CSSProperties = {
    flex: '1 1 120px',
    padding: '10px 18px',
    borderRadius: 24,
    fontWeight: 600,
    fontSize: 16,
    cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };

  return (
    <div
      className="confirmDialogBackdrop"
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 16, background: 'rgba(0, 0, 0, 0.55)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="confirmDialog"
        style={{
          position: 'relative', width: '100%', maxWidth: 420, boxSizing: 'border-box', padding: '24px 20px 20px',
          borderRadius: 12, background: colors.background, color: colors.text, textAlign: 'center',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)', border: `1px solid ${colors.text}33`,
        }}
      >
        <button
          type="button"
          aria-label="Close"
          className="confirmDialogClose"
          onClick={onCancel}
          disabled={busy}
          style={{
            position: 'absolute', top: 8, right: 8, width: 32, height: 32, margin: 0, padding: 0, border: 'none',
            background: 'transparent', color: colors.text, fontSize: 22, lineHeight: '32px', cursor: 'pointer',
          }}
        >
          ×
        </button>
        <div style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0 0 10px' }}>{title}</div>
        <p style={{ margin: '0 0 16px', fontSize: '0.95rem' }}>{message}</p>
        {notice && (
          <div
            role={noticeRole}
            style={{ margin: '0 0 14px', padding: '8px 10px', borderRadius: 6, fontSize: '0.9rem', textAlign: 'left',
              background: 'rgba(255, 255, 255, 0.12)', border: `1px solid ${colors.text}55` }}
          >
            {notice}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{ ...button, margin: 0, background: 'transparent', color: colors.text, border: `1px solid ${colors.text}` }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onOk}
            disabled={busy}
            style={{ ...button, margin: 0, border: 'none', background: colors.buttonBackground, color: colors.buttonText }}
          >
            {busy ? 'Working…' : okLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
